import React, { useEffect, useState } from "react";
import http from "../services/http";
import { resolveAssetUrl } from "./assetUrl";

/**
 * Giấy tờ riêng tư (27/10/2026): giấy phép, chứng từ thuế, chứng từ đối soát, bằng chứng
 * điều chỉnh số dư KHÔNG còn mở được bằng link công khai. Muốn xem phải hỏi backend
 * `GET /private-files/link` (kiểm quyền Admin theo thư mục) ⇒ nhận link đọc hạn 10 phút.
 *
 * Giá trị không thuộc 4 thư mục đó ⇒ giữ cách cũ (resolveAssetUrl).
 */
const PRIVATE_RE = /(^|\/)documents\/(station-licences|tax-legal|settlements|partner-balance-adjustments)\//;

export function isPrivateFilePath(value?: string | null): boolean {
	return PRIVATE_RE.test(String(value || ""));
}

/** Chỉ cho link http(s) hoặc đường dẫn trên host API — chặn `javascript:` và đồ lạ. */
function safeHref(value?: string | null): string {
	const url = resolveAssetUrl(value);
	return /^https?:\/\//i.test(url) || /^\/[^/]/.test(url) ? url : "";
}

/** Link đọc dùng ngay (hạn 10 phút). Lỗi / không có quyền ⇒ "". */
export async function getPrivateFileUrl(value: string): Promise<string> {
	if (!isPrivateFilePath(value)) return safeHref(value);
	try {
		const { data } = await http.get("/private-files/link", { params: { path: value } });
		return safeHref(data?.data?.url || "");
	} catch {
		return "";
	}
}

/**
 * Mở file ở tab mới. Mở tab TRƯỚC khi chờ mạng — mở sau `await` thì trình duyệt coi là
 * popup tự bật và chặn.
 */
export async function openPrivateFile(value: string): Promise<void> {
	const tab = window.open("", "_blank");
	const url = await getPrivateFileUrl(value);
	if (!url) {
		tab?.close();
		window.alert("Cannot open this file. Please try again.");
		return;
	}
	if (tab) {
		tab.opener = null;
		tab.location.href = url;
	} else {
		window.location.href = url;
	}
}

/** Link đọc cho ảnh thu nhỏ (<img src>). Rỗng trong lúc đang hỏi. */
export function usePrivateFileUrl(value?: string | null): string {
	const v = String(value || "");
	const [url, setUrl] = useState(() => (isPrivateFilePath(v) ? "" : safeHref(v)));
	useEffect(() => {
		let alive = true;
		if (!isPrivateFilePath(v)) {
			setUrl(safeHref(v));
			return;
		}
		setUrl("");
		getPrivateFileUrl(v).then((u) => alive && setUrl(u));
		return () => {
			alive = false;
		};
	}, [v]);
	return url;
}

type AnchorProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children"> & {
	path: string;
	/** Nhận link đọc hiện tại (để vẽ ảnh thu nhỏ). */
	children: React.ReactNode | ((url: string) => React.ReactNode);
};

/** Thẻ <a> cho file riêng tư: bấm là xin link mới rồi mở tab — link cũ có hết hạn cũng không sao. */
export function PrivateFileAnchor({ path, children, onClick, ...rest }: AnchorProps) {
	const url = usePrivateFileUrl(path);
	const priv = isPrivateFilePath(path);
	return (
		<a
			{...rest}
			href={url || undefined}
			target="_blank"
			rel="noreferrer"
			onClick={(e) => {
				onClick?.(e);
				if (!priv || e.defaultPrevented) return;
				e.preventDefault();
				void openPrivateFile(path);
			}}>
			{typeof children === "function" ? children(url) : children}
		</a>
	);
}

/** <img> cho ảnh riêng tư (vd ảnh thu nhỏ giấy phép). */
export function PrivateFileImg({ path, ...rest }: Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & { path: string }) {
	const url = usePrivateFileUrl(path);
	return <img {...rest} src={url || undefined} />;
}
